import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { ThemeProvider } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { PostShiftReviewFlow } from "@src/appV2/PostShiftReview/PostShiftReviewFlow";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { getCreditProgressBarLabel } from "../../../../EmergencyCredits/utils/getCreditProgressBarLabel";
import { MY_SHIFTS_FULL_PATH } from "../../../path";
import { type GradientVariant, type ScoreChange } from "./YourClipboardScore/types";
import { YourClipboardScorePage } from "./YourClipboardScore/YourClipboardScorePage";
import { YourEmergencyCreditsPage } from "./YourEmergencyCredits/YourEmergencyCreditsPage";

interface RouteParams {
  shiftId: string;
}

interface LocationState {
  facility?: {
    id: string;
    name: string;
    type: string;
  };
  isDelayedWorkplaceReviewEnabled?: boolean;
  backgroundGradient?: string;
  /**
   * Data for the YourClipboardScore page
   */
  clipboardScoreData?: {
    startScore: number;
    scoreChanges: ScoreChange[];
    subtitle: string;
    gradientVariant: GradientVariant;
  };
}

type FlowStep = "emergencyCredits" | "clipboardScore";

export function EmergencyCreditsClockOutFlowPage() {
  const { shiftId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const postShiftReviewModalState = useModalState();
  const emergencyCreditsEducationModalState = useModalState();

  const { userId: workerId } = useDefinedWorker();
  const { facility, isDelayedWorkplaceReviewEnabled, backgroundGradient, clipboardScoreData } =
    location.state ?? {};

  const [currentStep, setCurrentStep] = useState<FlowStep>("emergencyCredits");

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);
  const { emergencyCreditStats } = clipboardScoreProfile ?? {};

  const handleEmergencyCreditsContinue = useCallback((): void => {
    setCurrentStep("clipboardScore");
  }, []);

  const handleClipboardScoreContinue = useCallback((): void => {
    if ((isDelayedWorkplaceReviewEnabled ?? false) || !facility) {
      history.replace(MY_SHIFTS_FULL_PATH);
    } else {
      postShiftReviewModalState.openModal();
    }
  }, [history, isDelayedWorkplaceReviewEnabled, facility, postShiftReviewModalState]);

  const handleApplyCredit = useCallback((): void => {
    // TODO: Implement credit application logic
    handleClipboardScoreContinue();
  }, [handleClipboardScoreContinue]);

  const handlePostShiftReviewComplete = useCallback((): void => {
    postShiftReviewModalState.closeModal();
    history.replace(MY_SHIFTS_FULL_PATH);
  }, [history, postShiftReviewModalState]);

  const handleLearnMoreClick = useCallback((): void => {
    emergencyCreditsEducationModalState.openModal();
  }, [emergencyCreditsEducationModalState]);

  // Get credit stats or default values
  const cancellationStats = emergencyCreditStats?.cancellation ?? {
    completedShiftsTowardsNextCredit: 0,
    requiredShiftsForNextCredit: 20,
    totalAvailableCredits: 0,
  };

  const latenessStats = emergencyCreditStats?.lateness ?? {
    completedShiftsTowardsNextCredit: 0,
    requiredShiftsForNextCredit: 20,
    totalAvailableCredits: 0,
  };

  const hasLatenessCredit = latenessStats.totalAvailableCredits > 0;

  // Determine current count for display (for lateness progress bar in clipboard score page)
  // If credit is available but no progress yet, show bar as complete
  const latenessCurrentCount =
    hasLatenessCredit && latenessStats.completedShiftsTowardsNextCredit === 0
      ? latenessStats.requiredShiftsForNextCredit
      : latenessStats.completedShiftsTowardsNextCredit;

  // Default clipboard score data for development/testing
  // TODO: Replace with actual data from clock-out flow context
  const defaultClipboardScoreData = {
    startScore: 10,
    scoreChanges: [
      { label: "Completed shift", value: 5, color: "positive" as const },
      { label: "15-30 minutes late", value: -60, color: "negative" as const },
    ],
    subtitle: "You arrived 18 minutes late to this shift.",
    gradientVariant: "negative" as const,
  };

  const scoreData = clipboardScoreData ?? defaultClipboardScoreData;

  if (currentStep === "clipboardScore") {
    return (
      <ThemeProvider theme={getTheme()}>
        <YourClipboardScorePage
          showCreditProgressBar
          startScore={scoreData.startScore}
          scoreChanges={scoreData.scoreChanges}
          subtitle={scoreData.subtitle}
          gradientVariant={scoreData.gradientVariant}
          creditProgressBarProps={{
            currentCount: latenessCurrentCount,
            totalCount: latenessStats.requiredShiftsForNextCredit,
            label: getCreditProgressBarLabel("lateness", hasLatenessCredit),
            color: "green",
          }}
          hasLatenessCredit={hasLatenessCredit}
          onApplyCredit={handleApplyCredit}
          onContinue={handleClipboardScoreContinue}
        />

        {facility && (
          <PostShiftReviewFlow
            modalState={postShiftReviewModalState}
            facility={{
              id: facility.id,
              name: facility.name,
              type: facility.type,
              photo: undefined,
            }}
            shiftId={shiftId}
            onComplete={handlePostShiftReviewComplete}
          />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <YourEmergencyCreditsPage
        backgroundGradient={backgroundGradient}
        cancellationStats={cancellationStats}
        latenessStats={latenessStats}
        onContinue={handleEmergencyCreditsContinue}
        onLearnMoreClick={handleLearnMoreClick}
      />

      {facility && (
        <PostShiftReviewFlow
          modalState={postShiftReviewModalState}
          facility={{
            id: facility.id,
            name: facility.name,
            type: facility.type,
            photo: undefined,
          }}
          shiftId={shiftId}
          onComplete={handlePostShiftReviewComplete}
        />
      )}
    </ThemeProvider>
  );
}
